<script setup>
import { computed, ref, watch, onBeforeUnmount, nextTick } from 'vue'
import IconBase from '../icon/IconBase.vue'
import IconLinkExternal from '../icon/IconLinkExternal.vue'
import IconEyeOutline from '../icon/IconEyeOutline.vue'

const props = defineProps({
  // Whether a content source is set (controls preview vs. empty state)
  hasSource: {
    type: Boolean,
    default: false,
  },
  // Public protected-page URL (for the "Open page" link + browser bar context)
  protectedUrl: {
    type: String,
    default: '',
  },
  // srcdoc HTML that renders the embedded paywall
  srcdoc: {
    type: String,
    default: '',
  },
  // Bump to force the iframe to remount and re-fetch the latest paywall config
  refreshKey: {
    type: [Number, String],
    default: 0,
  },
})

const openPageUrl = computed(() => props.protectedUrl || '')

// The embedded widget renders a 16:9 player PLUS an account/"My content" bar
// below it once signed in. A rigid 16:9 frame clips that bar, so we size the
// preview iframe to its actual content height (never shorter than 16:9).
const frameRef = ref(null)
const frameHeight = ref(0)
let ro = null
const measureTimers = []

const measure = () => {
  const f = frameRef.value
  if (!f) return
  try {
    const doc = f.contentDocument
    const content = doc && doc.documentElement ? doc.documentElement.scrollHeight : 0
    // Keep at least a 16:9 video; the bar adds height on top of that.
    const minH = f.clientWidth ? Math.round((f.clientWidth * 9) / 16) : 0
    const next = Math.max(content, minH)
    if (next && Math.abs(next - frameHeight.value) > 1) frameHeight.value = next
  } catch (e) {
    /* cross-origin (shouldn't happen with allow-same-origin) — keep 16:9 */
  }
}

const clearTimers = () => {
  while (measureTimers.length) clearTimeout(measureTimers.pop())
}
const teardown = () => {
  if (ro) { try { ro.disconnect() } catch (e) { /* noop */ } ro = null }
  clearTimers()
}

const onFrameLoad = () => {
  measure()
  try {
    const doc = frameRef.value && frameRef.value.contentDocument
    if (doc && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => measure())
      ro.observe(doc.documentElement)
    }
  } catch (e) { /* noop */ }
  // The widget renders asynchronously (config fetch), so re-measure a few times.
  clearTimers()
  ;[150, 500, 1200, 2500].forEach((t) => measureTimers.push(setTimeout(measure, t)))
}

// Remount (refreshKey change) resets the measured height so it re-fits.
watch(() => props.refreshKey, () => {
  teardown()
  frameHeight.value = 0
  nextTick(measure)
})

onBeforeUnmount(teardown)
</script>

<template>
  <div class="lg:sticky lg:top-4 min-w-0">
    <div class="flex items-center justify-between gap-2 mb-2">
      <h3 class="text-sm font-semibold text-white mb-0">Live preview</h3>
      <a v-if="protectedUrl" :href="openPageUrl" target="_blank"
        class="inline-flex items-center gap-1 text-tiny font-medium text-main hover:underline">
        Open page
        <icon-base fill="none" width="13" height="13">
          <icon-link-external />
        </icon-base>
      </a>
    </div>
    <div class="rounded-xl overflow-hidden ring-1 ring-white/[0.05] bg-surface-1">
      <!-- Page preview: grows to fit the widget (video + account bar) -->
      <iframe v-if="hasSource && srcdoc" ref="frameRef" :key="'preview-' + refreshKey" :srcdoc="srcdoc"
        @load="onFrameLoad" class="block w-full bg-surface-1" frameborder="0" title="Paywall page preview"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups" loading="lazy"
        :style="frameHeight ? { height: frameHeight + 'px' } : { aspectRatio: '16 / 9' }"></iframe>
      <div v-else class="relative bg-surface-1" style="aspect-ratio: 16 / 9">
        <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
          <icon-base :width="28" :height="28" :width-view-box="24" :height-view-box="24" fill="none"
            class="text-surface-6">
            <icon-eye-outline />
          </icon-base>
          <p class="text-sm font-medium text-surface-8 mb-0">No preview yet</p>
          <p class="text-tiny text-surface-6 mb-0">Select a content source to preview your paywall.</p>
        </div>
      </div>
    </div>
    <p class="text-tiny text-surface-6 mb-0 mt-2">This is what your audience sees when you embed the paywall on your
      site.</p>
  </div>
</template>

<style scoped></style>
